import { JSX } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { useGetContentDetailPage } from "@/hooks/api/content.hooks";
import { cn } from "@/utils/cn";

export const formatTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const { t } = useTranslation();

  let result = "";
  if (hours > 0) {
    result += t(hours === 1 ? "hour" : "hours", { count: hours }) + " ";
  }
  if (minutes > 0 || hours > 0) {
    result += t(minutes === 1 ? "minute" : "minutes", { count: minutes });
  }
  return result.trim() || t("zero_minutes");
};

const DetailDescription = (): JSX.Element => {
  const { t } = useTranslation();
  const { id } = useParams() as { id: string };
  const { data } = useGetContentDetailPage(+id);
  const content = data.content;

  if (!data) return <></>;

  return (
    <section
      className={cn(
        "mt-6 grid grid-cols-2 gap-6 rounded-[2rem] bg-surface-bright p-8 max-xl:flex max-xl:flex-col max-xl:gap-4 max-lg:mt-4 max-lg:rounded-[1.25rem] max-lg:p-4 max-md:gap-0 max-md:border max-md:border-surface-bright max-md:bg-transparent max-md:p-3",
      )}
    >
      {content.description && (
        <article>
          <h2 className={"text-[1.875rem]/normal font-semibold max-xl:text-2xl/normal max-md:text-xl"}>
            {content.title}
          </h2>
          {content.original_name && (
            <h3 className={"mt-1 hidden text-sm text-on-surface max-md:block"}>{content.original_name}</h3>
          )}
          <p className={"mt-6 text-xl max-xl:mt-3 max-xl:text-base/normal max-xl:text-white max-md:text-base"}>
            {content.description}
          </p>
        </article>
      )}
      <div>
        <h2 className={"text-[1.875rem]/normal font-semibold max-xl:text-2xl/normal max-md:hidden"}>
          {t("General.information")}
        </h2>
        <table className={"mt-8 w-full max-xl:mt-3"}>
          <tbody className={"text-2xl/tight max-xl:text-base/normal "}>
            {content.country && (
              <tr className={"border-b border-b-surface-container-high"}>
                <th
                  scope="row"
                  className={"pb-2 pt-5 text-start text-xl font-normal  text-on-surface-variant max-md:text-sm"}
                >
                  {t("Detail.country")}
                </th>
                <td className="pb-2 pt-5 text-end text-xl max-md:text-sm">
                  <span>{content.country.map((c) => c.name).join(" / ")}</span>
                </td>
              </tr>
            )}
            {content.genres && (
              <tr className={"border-b border-b-surface-container-high"}>
                <th
                  scope="row"
                  className={"pb-2 pt-5 text-start text-xl font-normal text-on-surface-variant max-md:text-sm"}
                >
                  {t("Detail.genre")}
                </th>
                <td className={"pb-2 pt-5 text-end text-xl max-md:text-sm"}>
                  <span>{content.genres.map((g) => g.name).join(" / ")}</span>
                </td>
              </tr>
            )}
            {content.film?.duration && (
              <tr className={"border-b border-b-surface-container-high"}>
                <th
                  scope="row"
                  className={"pb-2 pt-5 text-start text-xl  font-normal text-on-surface-variant max-md:text-sm"}
                >
                  {t("Detail.duration")}
                </th>
                <td className={"pb-2 pt-5 text-end text-xl max-md:text-sm"}>{formatTime(content.film.duration)}</td>
              </tr>
            )}
            {(content.imdb_rating || content.kinopoisk_rating) && (
              <tr className={"border-b border-b-surface-container-high"}>
                <th
                  scope="row"
                  className={"pb-2 pt-5 text-start  text-xl font-normal text-on-surface-variant max-md:text-sm"}
                >
                  {t("Detail.rating")}
                </th>
                <td className={"pb-2 pt-5 text-end"}>
                  {content.imdb_rating && (
                    <span className={"rounded bg-[#FFFFFF1A] p-1 text-xl max-md:text-sm"}>
                      {content.imdb_rating.toFixed(1)} IMDb
                    </span>
                  )}
                  {content.kinopoisk_rating && (
                    <span className={"ml-2 rounded bg-[#FFFFFF1A] p-1 text-xl max-md:text-sm"}>
                      {content.kinopoisk_rating.toFixed(1)} КП
                    </span>
                  )}
                </td>
              </tr>
            )}
            {(content.is_4k || content.is_full_hd) && (
              <tr className={"border-b border-b-surface-container-high"}>
                <th
                  scope="row"
                  className={" pb-2 pt-5 text-start text-xl font-normal text-on-surface-variant max-md:text-sm"}
                >
                  {t("Detail.quality")}
                </th>
                <td className={"pb-2 pt-5 text-end"}>
                  {content.is_full_hd && (
                    <span className={"rounded bg-[#FFFFFF1A] p-1 text-xl max-md:text-sm"}>FullHD</span>
                  )}
                  {content.is_4k && (
                    <span className={"ml-2 rounded bg-[#FFFFFF1A] p-1 text-xl max-md:text-sm"}>4K</span>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default DetailDescription;
